import { ApolloClient, InMemoryCache, createHttpLink, from, CombinedGraphQLErrors } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import Cookie from 'js-cookie';
import { BACKEND_URI } from '@/config';
import { captureException } from '@/sentry';

// Apollo Client setup
const httpLink = createHttpLink({
  uri: `${BACKEND_URI}/graphql`,
});

const authLink = setContext((_, { headers }) => {
  const token = Cookie.get('token');
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// The server answers an unauthenticated or locked-account GraphQL call with a
// 401 carrying `extensions.code === 'UNAUTHENTICATED'`. That is an expected
// outcome of an expired token, not a fault worth an issue — every session that
// times out would otherwise open one.
const isUnauthenticated = (err: { extensions?: Record<string, unknown> }) =>
  err.extensions?.code === 'UNAUTHENTICATED';

// Apollo Client 4 replaced the split `graphQLErrors` / `networkError` fields
// with a single `error`; GraphQL errors arrive wrapped in CombinedGraphQLErrors.
const sentryErrorLink = onError(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors
      .filter((err) => !isUnauthenticated(err))
      .forEach((err) => {
        captureException(err, {
          type: 'GraphQLError',
          operationName: operation.operationName,
          path: err.path,
        });
      });
  } else {
    captureException(error, {
      type: 'NetworkError',
      operationName: operation.operationName,
    });
  }
});

export const apolloClient = new ApolloClient({
  link: from([sentryErrorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
